import React from 'react';
import cx from 'classnames';
import { eventClickHandler } from 'helpers/prematch';
import PropTypes from 'prop-types';

import { extractEventMarketTypes, isEmptyCollection, isSuspended, isVisible } from '@atlas/core';
import { useEventViewHandlers } from '@atlas/ui';

import eventShape from 'common/PropTypes/eventShape';

import SimpleOutcomesRow from './SimpleOutcomesRow';
import SimpleParametricMarketTypeDropdown from './SimpleParametricMarketTypeDropdown';

const getDropdownMarkets = markets => {
  if (!markets) return [];

  return markets.filter(market => isVisible(market));
};

const SimpleEventMarketTypes = props => {
  const { event, mainMarketTypes, suspended } = props;
  const isEventSuspended = suspended || isSuspended(event);
  const containerRef = React.useRef();
  const onClickHandler = useEventViewHandlers(event, eventClickHandler);

  const marketTypes = mainMarketTypes.reduce(
    (acc, marketTypeInstructions) => {
      if (acc.isEmpty) return acc;

      const [marketTypeInstruction] = marketTypeInstructions;
      if (!marketTypeInstruction) return acc;

      const extractedMarketTypes = extractEventMarketTypes(
        event.marketTypes,
        marketTypeInstructions,
      );

      const [{ markets, parameterized }] = extractedMarketTypes;
      let dropdownMarkets = [];

      if (parameterized) {
        const allMarkets = markets && Object.values(markets);

        dropdownMarkets = getDropdownMarkets(allMarkets);
      }
      const withDropdown = !isEmptyCollection(dropdownMarkets) && dropdownMarkets.length > 1;

      const OutcomesRowView = SimpleOutcomesRow({
        event,
        marketTypes: extractedMarketTypes,
        suspended: isEventSuspended,
      });

      const { isEmpty, view } = OutcomesRowView;

      if (isEmpty && !!acc.outcomeRows.length) {
        return {
          isEmpty,
          outcomeRows: acc.outcomeRows,
        };
      }

      const marketRowClassName = cx('event-markets-row', { 'with-dropdown': withDropdown });

      const newOutcomeRow = (
        <div
          className={marketRowClassName}
          key={marketTypeInstruction.periodTypeId}
          onClick={onClickHandler}
          ref={containerRef}
          role="button"
        >
          {view}
          {withDropdown && (
            <SimpleParametricMarketTypeDropdown
              containerRef={containerRef}
              event={event}
              marketType={marketTypeInstruction}
              markets={dropdownMarkets}
              suspended={suspended}
            />
          )}
        </div>
      );

      return {
        isEmpty,
        outcomeRows: [...acc.outcomeRows, newOutcomeRow],
      };
    },
    {
      isEmpty: false,
      outcomeRows: [],
    },
  );

  return marketTypes.outcomeRows;
};

SimpleEventMarketTypes.propTypes = {
  event: eventShape.isRequired,
  mainMarketTypes: PropTypes.array.isRequired,
  suspended: PropTypes.bool,
};

export default SimpleEventMarketTypes;
